import React, { useState, useEffect, createRef } from "react";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import moment from "moment";
import { filter } from "lodash";

import StopsMapbox from "../Mapbox/StopsMapbox";
import RouteChart from "../RouteChart";
import RouteDispatch from "../RouteDispatch";
import SearchStops from "../SearchStops";
import StopsSelected from "../StopsSelected";

import { DATE_FORMAT_FRONTEND } from "../../constants";
import "./RoutePlanChart.scss";
import { getTranslation } from "../../utils/helper";
import StopsClicked from "../StopsClicked";
import useRefDimensions from "../../hooks/useRefDimensions";
import { Button, Form, Modal } from "antd";
import RoutePlanForm from "../Forms/RoutePlans/RoutePlan";

const RoutePlanChart = ({
  routePlan,
  routes,
  stops,
  stopClicked,
  onClickStop,
  routeClicked,
  onClickRoute,
  stepsHovered,
  setStepsHovered,
  onClosePopup,
  doOptimizeRoutePlan,
  doReorderRoute,
  doDispatchRoutePlan,
  doLockDriver,
  doDetailRoutePlan,
  doEditDriver,
  setting,
  stepClicked,
  doDuplicateRoutePlan,
  isFetching,
}) => {
  const [driverIdsHide, setDriverIdsHide] = useState([]);
  const [isOptimize, setIsOptimize] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [form] = Form.useForm();
  const [routeType, setRouteType] = useState(null);
  const [chartType, setChartType] = useState("timeline");
  const [showStopContent, setShowStopContent] = useState(undefined);
  const [showMoreRoute, setShowMoreRoute] = useState(false);
  const [selectedStops, setSelectedStops] = useState([]);
  const [isBulkUpdateStopsSelected, setIsBulkUpdateStopsSelected] = useState(false);
  const [driverIdsLock, setDriverIdsLock] = useState([]);
  const chartRef = createRef();
  const dimensionsChart = useRefDimensions(chartRef);
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    routePlan: null,
  });

  useEffect(() => {
    doDetailRoutePlan(routePlan._id);
  }, [chartType]);

  const reloadData = () => {
    doDetailRoutePlan(routePlan._id);
  };

  const handleClickDuplicate = async item => {
    setState({
      ...state,
      action: "duplicate",
      routePlan: item,
      visibleModal: true,
    });
  };

  const handleOnFinish = async values => {
    const { payload } = await doDuplicateRoutePlan({
      id: state.routePlan._id,
      ...values,
    });
    if (payload?._id) {
      window.open(`/route-plans/${payload?._id}/orders`, "_blank");
    }
    setIsOptimize(true);
    handleCancel();
  };

  const handleCancel = () => {
    setState({ ...state, visibleModal: false, action: null });
    form.resetFields();
  };

  const handleUpdateDriver = params => {
    doEditDriver({ id: routePlan._id, ...params });
  };

  return (
    <div className="ContentMain RoutePlanChart">
      <div className="MainLeft__Header">
        <div className="MainLeft__Header__Title">
          <Link to="/route-plans">{getTranslation(setting, "Route Plans", "Route Plans")}</Link>
          <ReactSVG className="icon" src="/icons/arrow-forward.svg" />
          <div className="Link-Wrapper">
            <p>
              {routePlan?.name}(
              <span className="Capitalize">
                {moment(routePlan?.deliveryDate).utc().locale(setting.systemLanguage).format(DATE_FORMAT_FRONTEND)}
              </span>
              )
            </p>
            <Button onClick={() => handleClickDuplicate(routePlan)} type="link" className="duplicate">
              <ReactSVG className="icon" src="/icons/control_point_duplicate.svg" />
            </Button>
          </div>
        </div>
      </div>
      {isBulkUpdateStopsSelected && (
        <StopsSelected
          routePlan={routePlan}
          stops={selectedStops}
          routes={routes}
          setIsBulkUpdateStopsSelected={setIsBulkUpdateStopsSelected}
          setting={setting}
        />
      )}
      {stopClicked?.lat && stopClicked?.lng && (
        <StopsClicked
          stopClicked={stopClicked}
          stops={filter(stops, s => s.lat && s.lng)}
          routes={routes}
          onClickStop={onClickStop}
          onClosePopup={onClosePopup}
          driverIdsHide={driverIdsHide}
          orders={routePlan?.orders || []}
          setShowStopContent={setShowStopContent}
          dimensionsChart={dimensionsChart}
        />
      )}
      <SearchStops stops={stops} onClickStop={onClickStop} setClicked={setClicked} setting={setting} />
      <StopsMapbox
        routes={routes}
        stops={filter(stops, s => s.lat && s.lng)}
        stopClicked={stopClicked}
        onClickStop={onClickStop}
        routeClicked={routeClicked}
        onClickRoute={onClickRoute}
        onClosePopup={onClosePopup}
        clicked={clicked}
        setClicked={setClicked}
        driverIdsHide={driverIdsHide}
        routeType={routeType}
        setRouteType={setRouteType}
        showMoreRoute={showMoreRoute}
        setShowMoreRoute={setShowMoreRoute}
        orders={routePlan?.orders || []}
        showToolbarDraw={true}
        setIsBulkUpdateStopsSelected={setIsBulkUpdateStopsSelected}
        selectedStops={selectedStops}
        setSelectedStops={setSelectedStops}
        doLockDriver={doLockDriver}
        routePlan={routePlan}
        driverIdsLock={driverIdsLock}
        setDriverIdsLock={setDriverIdsLock}
        showStopContent={showStopContent}
        setShowStopContent={setShowStopContent}
        stepClickedParams={stepClicked}
      />
      {chartType === "timeline" && (
        <RouteChart
          routePlan={routePlan}
          isOptimize={isOptimize}
          setIsOptimize={setIsOptimize}
          routes={routes}
          stops={stops}
          onClickStop={onClickStop}
          onClickRoute={onClickRoute}
          stepsHovered={stepsHovered}
          setStepsHovered={setStepsHovered}
          clicked={clicked}
          setClicked={setClicked}
          driverIdsHide={driverIdsHide}
          setDriverIdsHide={setDriverIdsHide}
          doOptimizeRoutePlan={doOptimizeRoutePlan}
          doReorderRoute={doReorderRoute}
          doDispatchRoutePlan={doDispatchRoutePlan}
          doLockDriver={doLockDriver}
          onClosePopup={onClosePopup}
          routeType={routeType}
          setRouteType={setRouteType}
          setChartType={setChartType}
          setShowMoreRoute={setShowMoreRoute}
          setting={setting}
          driverIdsLock={driverIdsLock}
          setDriverIdsLock={setDriverIdsLock}
          chartRef={chartRef}
          stepClicked={stepClicked}
        />
      )}
      {chartType === "dispatch" && (
        <RouteDispatch
          routePlan={routePlan}
          routes={routes}
          setChartType={setChartType}
          driverIdsHide={driverIdsHide}
          setDriverIdsHide={setDriverIdsHide}
          doLockDriver={doLockDriver}
          handleUpdateDriver={handleUpdateDriver}
          reloadData={reloadData}
          setting={setting}
        />
      )}
      <Modal open={state.visibleModal} onCancel={handleCancel} footer={[]} width={370}>
        <RoutePlanForm
          form={form}
          isFetching={isFetching || isOptimize}
          routePlan={state.routePlan}
          handleSubmit={handleOnFinish}
          handleCancel={handleCancel}
          isDuplicate={state.action === "duplicate"}
        />
      </Modal>
    </div>
  );
};

export default RoutePlanChart;
